import { colorCharts } from "data/palettes";
import React from "react";
import { useTranslation } from "react-i18next";
import { Cell, Pie, PieChart, Tooltip, TooltipProps } from "recharts";
import colorsStore from "shared/store/colorsStore";
import styles from "./currencyExposure.module.scss";

type CustomTooltipProps = TooltipProps<number, string> & {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    payload: { label: string; weight: number };
  }>;
};

const formatPercent = (value) => {
  if (typeof value !== "number") return "0";
  const formatted = value.toFixed(2);
  return formatted === "0.00" && value > 0
    ? "< 0.01"
    : formatted === "100.00" && value < 100
      ? "> 99.99"
      : formatted;
};

const CurrencyTable = ({ data }) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  return (
    <div className={styles.tableContent}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.headerCell}>Macro Asset class</th>
            <th className={styles.headerCell}>Peso %</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => {
            return (
              <tr key={index} className={styles.tableRow}>
                <td className={styles.labelCell}>
                  <span
                    className={styles.dot}
                    style={{
                      backgroundColor:
                        colorsConfig[colorCharts[index % colorCharts.length]],
                    }}
                  />
                  {item.label}
                </td>
                <td className={styles.valueCell}>
                  {formatPercent(item.weight)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className={styles.tooltip}>
        <p className={styles.tooltipLabel}>{payload[0].name}</p>
        <p className={styles.tooltipValue}>
          {formatPercent(payload[0].value)}%
        </p>
      </div>
    );
  }
  return null;
};

const DonutChart = ({ data }) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  return (
    <div
      className={styles.pieChartContainer} // ✅ This must have same rules as .pieChartContainer in FinecoPieChart
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        maxHeight: "400px",
        minHeight: "200px",
        padding: "24px 40px",
      }}
    >
      <PieChart width={200} height={200}>
        <Pie
          data={data}
          dataKey="weight"
          nameKey="label"
          cx="50%"
          cy="50%"
          innerRadius="70%"
          outerRadius="100%"
          isAnimationActive={false}
        >
          {data.map((entry, index) => (
            <Cell
              key={index}
              fill={colorsConfig[colorCharts[index % colorCharts.length]]}
              stroke="white"
              strokeWidth={2}
            />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
      </PieChart>
    </div>
  );
};

const CurrencyExposureContainer = ({ data }) => {
  const { t } = useTranslation();
  const nonZeroData = data.filter((item) => item.weight > 0);

  return (
    <div className={styles.sectionContainer}>
      <h3 className={styles.title}>{t("productCard.esposizioneValutaria")}</h3>
      <div className={styles.chartsContainer}>
        <CurrencyTable data={nonZeroData} />
        <DonutChart data={nonZeroData} />
      </div>
    </div>
  );
};

export default CurrencyExposureContainer;
